import { type ChangeEvent, type ReactNode, useEffect, useLayoutEffect, useRef } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { nextOrdering, sortDirection } from './data-table-sort'

export interface Column<Row> {
  /** Header label. */
  header: ReactNode
  /** Cell renderer. */
  cell: (row: Row) => ReactNode
  /** Optional extra className for both the header and body cell. */
  className?: string
  /**
   * Backend `ordering` field name (DRF `ordering_fields`). When set and the table
   * has an `ordering`/`onOrderingChange` pair, the header becomes a sort toggle
   * (asc → desc → cleared). Omit for non-sortable columns (e.g. class counters,
   * which have no DB column).
   */
  sortKey?: string
  /** Accessible name for sorting when the visible header combines multiple fields. */
  sortLabel?: string
}

function DebouncedSearchInput({
  search,
  onSearchChange,
  placeholder,
}: {
  search: string
  onSearchChange: (search: string) => void
  placeholder: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastEmittedSearchRef = useRef(search)
  const onSearchChangeRef = useRef(onSearchChange)

  useLayoutEffect(() => {
    onSearchChangeRef.current = onSearchChange
  }, [onSearchChange])

  useEffect(() => {
    if (search === lastEmittedSearchRef.current) return

    lastEmittedSearchRef.current = search
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = null
    if (inputRef.current) inputRef.current.value = search
  }, [search])

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    },
    [],
  )

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    const value = event.currentTarget.value
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      if (value === lastEmittedSearchRef.current) return
      lastEmittedSearchRef.current = value
      onSearchChangeRef.current(value)
    }, 300)
  }

  return (
    <div className="relative w-full max-w-xs">
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        defaultValue={search}
        onChange={handleChange}
        placeholder={placeholder}
        className="pl-8"
      />
    </div>
  )
}

export interface StateOption<S extends string> {
  value: S
  label: string
}

interface DataTableProps<Row, S extends string> {
  columns: Column<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string | number
  /** Total server-side row count (DRF `count`), drives the pager. */
  count: number
  page: number
  onPageChange: (page: number) => void
  pageSize: number
  /** Controlled search text; debounced before `onSearchChange` fires. */
  search: string
  onSearchChange: (search: string) => void
  searchPlaceholder?: string
  /** Hide the search box entirely (for lists with no name-search param). */
  hideSearch?: boolean
  /** State filter pills (Wszystkie/Aktywne/…); omit to hide. */
  stateValue?: S
  stateOptions?: StateOption<S>[]
  onStateChange?: (state: S) => void
  /** Extra filter controls rendered next to the state pills (e.g. a school select). */
  filters?: ReactNode
  /**
   * Current DRF `ordering` value (e.g. `name` or `-city`); paired with
   * `onOrderingChange` to make columns with a `sortKey` clickable sort toggles.
   */
  ordering?: string
  onOrderingChange?: (ordering: string) => void
  isLoading?: boolean
  /** HTTP or transport failure; kept distinct from a successful empty result. */
  isError?: boolean
  errorMessage?: string
  onRetry?: () => void
  emptyMessage?: string
  /** Optional row click handler (e.g. navigate to detail). */
  onRowClick?: (row: Row) => void
  /** Singular/plural noun for the count label, e.g. ['szkoła','szkół']. */
  countNoun?: [string, string]
}

/**
 * Server-paginated table wrapper over the shadcn `Table` primitives. Owns the
 * search box (debounced), the state-filter pills (extracted from the original
 * hand-rolled klasy markup) and a page-number pager; all of it drives the parent's
 * TanStack Query params. Deliberately no TanStack Table dependency — the panel's
 * tables are simple, server-driven lists.
 */
export function DataTable<Row, S extends string = string>({
  columns,
  rows,
  rowKey,
  count,
  page,
  onPageChange,
  pageSize,
  search,
  onSearchChange,
  searchPlaceholder = 'Szukaj…',
  hideSearch,
  stateValue,
  stateOptions,
  onStateChange,
  filters,
  ordering,
  onOrderingChange,
  isLoading,
  isError,
  errorMessage = 'Nie udało się pobrać danych. Spróbuj ponownie.',
  onRetry,
  emptyMessage = 'Brak wyników.',
  onRowClick,
  countNoun,
}: DataTableProps<Row, S>) {
  const totalPages = Math.max(1, Math.ceil(count / pageSize))
  const countLabel = countNoun
    ? `${count} ${count === 1 ? countNoun[0] : countNoun[1]}`
    : `${count}`
  const hasStateFilters = Boolean(stateOptions && onStateChange)
  const hasToolbar = !hideSearch || hasStateFilters || filters != null

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {hasToolbar && (
        <div
          data-slot="data-table-toolbar"
          className="flex min-w-0 flex-wrap items-center justify-between gap-3"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {stateOptions && onStateChange && (
              <div className="flex gap-2">
                {stateOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onStateChange(opt.value)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      stateValue === opt.value
                        ? 'border-transparent bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {filters}
          </div>
          {!hideSearch && (
            <DebouncedSearchInput
              search={search}
              onSearchChange={onSearchChange}
              placeholder={searchPlaceholder}
            />
          )}
        </div>
      )}

      <div className="min-w-0 overflow-hidden rounded-xl border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col, i) => {
                const sortable = col.sortKey && onOrderingChange
                const dir = col.sortKey ? sortDirection(ordering ?? '', col.sortKey) : null
                const sortLabel =
                  col.sortLabel ?? (typeof col.header === 'string' ? col.header : 'kolumnie')
                return (
                  <TableHead
                    key={i}
                    className={col.className}
                    aria-sort={
                      sortable
                        ? dir === 'asc'
                          ? 'ascending'
                          : dir === 'desc'
                            ? 'descending'
                            : 'none'
                        : undefined
                    }
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onOrderingChange(nextOrdering(ordering ?? '', col.sortKey!))}
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        aria-label={`Sortuj po: ${sortLabel}`}
                      >
                        {col.header}
                        {dir === 'asc' ? (
                          <ChevronUp className="size-3.5" />
                        ) : dir === 'desc' ? (
                          <ChevronDown className="size-3.5" />
                        ) : (
                          <ChevronsUpDown className="size-3.5 text-muted-foreground/50" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  Ładowanie…
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center">
                  <div role="alert" className="flex flex-col items-center gap-3 text-destructive">
                    <span>{errorMessage}</span>
                    {onRetry ? (
                      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                        Spróbuj ponownie
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(onRowClick && 'cursor-pointer')}
                >
                  {columns.map((col, i) => (
                    <TableCell key={i} className={col.className}>
                      {col.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isError && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <p>{countLabel}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              title="Poprzednia strona"
            >
              <ChevronLeft />
            </Button>
            <span className="tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              title="Następna strona"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
